import { useState, type KeyboardEvent, type MouseEvent, type ChangeEvent } from 'react';
import { useGoogleLogin, TokenResponse, CodeResponse } from '@react-oauth/google';

import { useMainContext } from 'routes/MainContextProvider';
import isOnLineGmail from 'functions/general/isOnLineGmail';
import onClickPreventDefault from 'functions/general/onClickPreventDefault';
import InputPassword from 'components/atoms/InputPassword';
import useFocus from 'hooks/useFocus';
import validateEmail from 'functions/general/validateEmail';
import validatePassword from 'functions/general/validatePassword';
import gmailIcon from 'images/signUpLarge/gmail.svg';
import tigimIcon from 'images/tigim.svg';
import ModalForgot from './ModalForgot';
import Modal from './Modal';
import {
  ModalBodySignIn,
  InputModal,
  FormDiv,
  LoginDiv,
  ButtonSignIn,
  ButtonLogIn,
  SeparatorDiv,
  OptionsDiv,
  ButtonGmailLoginDiv
} from './elements';
import type { ModalSignInProps } from './types';

const ModalSignIn = ({onClose, onSend, onSendGmail, onForgot, defaultEmail}: ModalSignInProps) => {
  const [email, setEmail] = useState<string>(defaultEmail);
  const [password, setPassword] = useState<string>('');
  const [emailRef, setEmailFocus] = useFocus<HTMLInputElement>();
  const [passwordRef, setPasswordFocus] = useFocus<HTMLInputElement>();
  const [forgotPassword, setForgotPassword] = useState<boolean>(false);
  const { toastr } = useMainContext();

  const isValid = () =>
    validateEmail(email) && validatePassword(password);

  const handleSend = () => {
    if (!validateEmail(email)) return setEmailFocus();
    if (!validatePassword(password)) return setPasswordFocus();
    onSend({ email, password }).then(response => {
      if (response) onClose();
    });
  };

  const onSuccess = (googleUser: TokenResponse | CodeResponse) => {
    if (isOnLineGmail(googleUser)) {
      onSendGmail(googleUser).then(response => {
        if (response) onClose();
      });
    }
  };

  const onError = (gmailError: Pick<TokenResponse, 'error' | 'error_description' | 'error_uri'>) => {
    if (gmailError.error) {
      toastr.error('', gmailError.error);
    }
  };
  const signIn = useGoogleLogin({
    onSuccess,
    onError
  });
  const login = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    signIn();
  }
  const onForgotPassword = () => {
    setForgotPassword(true);
  }
  const handleForgot = async (emailValue: string) => {
    setForgotPassword(false);
    await onForgot(emailValue);
  }
  const onReturn = () => {
    setForgotPassword(false);
  }
  const onKeyUp = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };
  if (forgotPassword) {
    return (
      <ModalForgot
        onClose={onClose}
        onSend={handleForgot}
        onReturn={onReturn} />
    );
  }
  return (
    <Modal onClose={onClose}>
      <ModalBodySignIn onClick={onClickPreventDefault}>
        <button
          className="close"
          type="button"
          aria-label="Close"
          onClick={onClose}>
          <span aria-hidden="true">×</span>
        </button>
        <img src={tigimIcon} alt="tigim" />
        <h5>Log into your account</h5>
        <FormDiv>
          <OptionsDiv>
            <ButtonGmailLoginDiv onClick={login}>
              <img src={gmailIcon} alt="gmail" />
              <span>Sign in with Google</span>
            </ButtonGmailLoginDiv>
          </OptionsDiv>
          <SeparatorDiv>
            <hr /><span>OR</span><hr />
          </SeparatorDiv>
          <InputModal
            ref={emailRef}
            id="email"
            name="email"
            placeholder="Email"
            className={validateEmail(email)? '': 'empty'}
            value={email}
            disabled={!!defaultEmail}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}/>
          <InputPassword
            reference={passwordRef}
            id="password"
            placeholder="Password"
            value={password}
            onKeyUp={onKeyUp}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}/>
          <ButtonSignIn
            className={isValid()? 'active': 'inactive'}
            type="button"
            aria-label="Sign in"
            onClick={handleSend}>
            LOGIN
          </ButtonSignIn>
          <LoginDiv>
            <ButtonLogIn onClick={onForgotPassword}>
              Forgot password?
            </ButtonLogIn>
          </LoginDiv>
        </FormDiv>
      </ModalBodySignIn>
    </Modal>
  );
};

export default ModalSignIn;
